import React, { useState } from "react";
import PolicyRulesDataGrid from "../DataGrids/PolicyRulesDataGrid";
import ActionsFragment from "../Fragments/ActionsFragment";
import UseCasesActionsFragment from "../Fragments/UseCasesActionsFragment";
import StartDateFragment from "../Fragments/StartDateFragment";
import {
  FormControl,
  IconButton,
  TextField,
  Box,
  Button,
  Typography,
} from "@mui/material";
import { AddCircle, Tornado } from "@mui/icons-material";
import {
  INCOMPATIBILITY_RFG,
  INCOMPATIBILITY_RFG_MEFIRESTCOME,
  INCOMPATIBILITY_RFG_TAB,
} from "../Constants/PolicyTranslationConstants";
import ProcedureCodeDialog from "../../PolicyTranslation/Dialogs/ProcedureCodeDialog";
import DxLimitationSelectFragment from "../Fragments/DxLimitationSelectFragment";
import DuplicateRowFragment from "../Fragments/DuplicateRowFragment";
import { useLocation } from "react-router-dom";
import styled from "@emotion/styled";
import { getLobName } from "../Utils/TranslationUtils";
import { renderCellClass, renderError } from "../Validation/ValidationHelper";
import { showVarianceIcon } from "utils/shared";
import StyledTornadoIcon from "components/TornadoIcon";

const AddProcedureCode = styled("a")(({ theme }) => {
  return {
    textTransform: "uppercase" /* Make text uppercase */,
    color: "blue" /* Standard hyperlink color */,
    pointerEvents: "none" /* Make the link not clickable */,
    cursor: "default" /* Change cursor to default */,
  };
});

const IncompatibilityTable = ({
  gridHeight,
  setLoading,
  updateRulesAfterEdit,
  availableProcedureCodes,
  chips,
  editable,
  newRowStructure,
  rows,
  setSelectedCC,
  addNewRows,
  duplicateRow,
  selectedCC,
  handleDelete,
  policyData,
  pageComponent,
  dxLimitationValues,
  handleRemoveInvalidRow,
  endExistingRule,
}) => {
  const [isPxDialogOpen, setIsPxDialogOpen] = useState(false);
  const [openDxDialog, setOpenDxDialog] = useState(false);
  const { state } = useLocation();
  const selectedHealthPlan = state?.policyState?.healthPlanName;

  const handleAddProcedureCode = () => {
    setIsPxDialogOpen(true);
  };

  const handleAddRowToGrid = (checkedItems) => {
    addNewRows(checkedItems, INCOMPATIBILITY_RFG_MEFIRESTCOME);
    addNewRows(checkedItems, INCOMPATIBILITY_RFG);
    setOpenDxDialog(false);
  };

  const handleProcCodeExistsTextChange = (value, params) => {
    let newRow = structuredClone(params.row);
    newRow.RuleFacts.MutuallyExclusivewithSameClaim = value;
    updateRulesAfterEdit(newRow, params.row);
  };

  const renderProcCodeExistsTextField = (params) => (
    <FormControl>
      <TextField
        label="Procedure Code"
        disabled={!editable}
        value={params.row.RuleFacts.MutuallyExclusivewithSameClaim || ""}
        onChange={(e) => {
          handleProcCodeExistsTextChange(e.target.value, params);
        }}
        inputProps={{
          maxLength: 5,
          style: { textAlign: "center", padding: "7.5px 10px" },
        }}
        InputLabelProps={{ shrink: true }}
      />
    </FormControl>
  );

  const columns = [
    {
      display: "flex",
      field: "CoverageCriteriaList",
      headerName: "CC#",
      flex: 0.6,
      minWidth: 125,
      editable: editable,
      valueGetter: (value, row) => {
        return row.RuleFacts.CoverageCriteriaList?.trim() || "";
      },
      valueSetter: (value, row) => {
        let newRow = structuredClone(row);
        newRow.RuleFacts.CoverageCriteriaList = value ? value?.trim() : "";
        return newRow;
      },
      cellClassName: (params) => renderCellClass(params),
      renderCell: (params) => {
        const { colDef, row } = params;
        const error = renderError(colDef.field, row);

        return (
          <Box display="flex" alignItems="center">
            <Typography sx={{ mr: 1 }}>
              {error
                ? error
                : Array.isArray(params.value)
                  ? params.value.join(",")
                  : params.value}
            </Typography>
          </Box>
        );
      },
    },
    {
      display: "flex",
      field: "denyProcedureCode",
      headerName: "Deny Procedure Code",
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => (
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <DuplicateRowFragment
            editable={editable && pageComponent === "PolicyTranslation"}
            params={params}
            value={params.row.RuleFacts.ProcedureCode}
            duplicateRow={duplicateRow}
          />
          {/* This is used to identify variances in new rules and rules that
                      have completed the sheet generation process */}
          {editable &&
            showVarianceIcon(params.row.RuleHeader, selectedHealthPlan) && (
              <StyledTornadoIcon />
            )}
        </Box>
      ),
      valueGetter: (value, row) => row.RuleFacts.ProcedureCode,
    },
    {
      display: "flex",
      field: "procedureCodeExists",
      headerName: "If Procedure Code Exists",
      flex: 0.8,
      minWidth: 110,
      editable: editable,
      valueGetter: (value, row) => {
        return row.RuleFacts.MutuallyExclusivewithSameClaim || "";
      },
      cellClassName: (params) => renderCellClass(params),
      renderCell: (params) => {
        const { colDef, row } = params;
        const error = renderError(colDef.field, row);
        if (error) {
          return error;
        }
        return renderProcCodeExistsTextField(params);
      },
      renderEditCell: (params) => {
        return renderProcCodeExistsTextField(params);
      },
    },
    {
      display: "flex",
      field: "dxLimitation",
      headerName: "Dx Limitation",
      flex: 1.1,
      minWidth: 240,
      renderCell: (params) => (
        <DxLimitationSelectFragment
          updateRulesAfterEdit={updateRulesAfterEdit}
          editable={editable && pageComponent === "PolicyTranslation"}
          params={params}
          options={dxLimitationValues}
        />
      ),
    },
    {
      display: "flex",
      field: "startDate",
      headerName: "Date & LOB Configuration",
      flex: 1.1,
      minWidth: 220,
      editable: editable,
      cellClassName: (params) => renderCellClass(params),
      renderCell: (params) => {
        const { colDef, row } = params;
        const error = renderError(colDef.field, row);

        if (error) {
          return error;
        }

        return (
          <StartDateFragment
            updateRulesAfterEdit={updateRulesAfterEdit}
            handleRemoveInvalidRow={handleRemoveInvalidRow}
            editable={editable && pageComponent === "PolicyTranslation"}
            params={params}
            policyData={policyData}
            endExistingRule={endExistingRule}
          />
        );
      },

      renderEditCell: (params) => {
        return (
          <StartDateFragment
            updateRulesAfterEdit={updateRulesAfterEdit}
            handleRemoveInvalidRow={handleRemoveInvalidRow}
            editable={editable && pageComponent === "PolicyTranslation"}
            params={params}
            policyData={policyData}
          />
        );
      },
    },
    {
      display: "flex",
      field: "LobDisplayName",
      headerName: "LOB",
      minWidth: 150,
      valueGetter: (value, row) => {
        return getLobName(row.RuleFacts.LobConfigKey, policyData);
      },
      cellClassName: (params) => renderCellClass(params),

      renderCell: (params) => {
        const { colDef, row } = params;
        const error = renderError(colDef.field, row);
        if (error) {
          return error;
        }
        return params.value || "";
      },
    },
    {
      display: "flex",
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        return pageComponent === "PolicyTranslation" ? (
          <ActionsFragment
            handleDelete={handleDelete}
            editable={editable}
            params={params}
          />
        ) : (
          <UseCasesActionsFragment
            editable={editable && pageComponent === "UseCases"}
            params={params}
          />
        );
      },
    },
  ];

  return (
    <div style={{ width: "100%" }}>
      {pageComponent !== "UseCases" && (
        <Box>
          <IconButton
            disabled={!editable}
            onClick={handleAddProcedureCode}
            color="primary"
            aria-label="add new row"
          >
            <AddCircle />
          </IconButton>
          <AddProcedureCode>Add Procedure Code</AddProcedureCode>
        </Box>
      )}
      <PolicyRulesDataGrid
        gridHeight={gridHeight}
        setLoading={setLoading}
        editable={editable && pageComponent === "PolicyTranslation"}
        rows={rows}
        columns={columns}
        initialState={{
          sorting: {
            sortModel: [{ field: "denyProcedureCode", sort: "asc" }],
          },
        }}
        processRowUpdate={(newRow, oldRow) =>
          updateRulesAfterEdit(newRow, oldRow)
        }
      />
      <ProcedureCodeDialog
        availableProcedureCodes={availableProcedureCodes}
        selectedCC={selectedCC}
        setSelectedCC={setSelectedCC}
        chips={chips}
        open={isPxDialogOpen}
        onClose={() => setIsPxDialogOpen(false)}
        onSave={handleAddRowToGrid}
      />
    </div>
  );
};

export default IncompatibilityTable;
